import type { Address } from 'viem'
import type { MulticallRetryFunction } from '../../types'
import {
  GmxDataStoreAbi,
  GmxErc20BalanceAbi,
  GmxGlvReaderAbi,
  GmxReaderAbi,
} from './abis'
import { getGmxReadContracts } from './contracts'
import { accountDepositListKey, accountWithdrawalListKey } from './keys'
import type {
  GmxPendingDeposit,
  GmxPendingWithdrawal,
  GmxUserBalance,
  GmxUserPositions,
  GmxUserPositionsOptions,
} from './types'

const DEFAULT_CAP = 50
const isBig = (v: unknown): v is bigint => typeof v === 'bigint'
const str = (v: unknown): string => (isBig(v) ? v.toString() : '0')

/**
 * Read a user's GMX position state on a chain: optional GM/GLV token
 * balances plus all pending (keeper-unexecuted) deposit/withdrawal
 * "tickets".
 *
 * GM tickets aren't exposed by a batch reader, so we enumerate the user's
 * request-key sets from the `DataStore` (`accountDeposit/WithdrawalListKey`)
 * and resolve each key via `Reader.getDeposit/getWithdrawal`. GLV tickets
 * have a convenience batch reader (`GlvReader.getAccountGlv*`), so those
 * are read directly. Balances are a plain `balanceOf` multicall.
 *
 * All sub-fetches run concurrently and degrade independently — a failing
 * reader yields an empty slice rather than sinking the whole call. Returns
 * empty arrays on unsupported chains.
 */
export const getGmxUserPositions = async (
  chainId: string,
  account: string,
  multicallRetry: MulticallRetryFunction,
  options?: GmxUserPositionsOptions,
): Promise<GmxUserPositions> => {
  const empty: GmxUserPositions = {
    chainId,
    account: account.toLowerCase(),
    balances: [],
    pendingDeposits: [],
    pendingWithdrawals: [],
  }
  const c = getGmxReadContracts(chainId)
  if (!c) return empty

  // Lowercase up front: viem's address encoder rejects mixed-case
  // non-checksummed inputs, and the key derivation must use a canonical form.
  const acct = account.toLowerCase() as Address
  const cap = BigInt(options?.cap ?? DEFAULT_CAP)
  const tokens = options?.tokens ?? []

  const safe = async <T>(p: Promise<T>, fallback: T): Promise<T> => {
    try {
      return await p
    } catch {
      return fallback
    }
  }

  // Run the three independent reads concurrently:
  //  - ERC-20 balanceOf for the caller's GM/GLV tokens
  //  - GLV pending deposits + withdrawals (batch GlvReader)
  //  - GM request-key sets from the DataStore
  const [balanceRes, glvRes, keyRes] = await Promise.all([
    tokens.length
      ? safe(
          multicallRetry({
            chain: chainId,
            calls: tokens.map((t) => ({
              address: t.address,
              name: 'balanceOf',
              params: [acct],
            })),
            abi: GmxErc20BalanceAbi as any,
            maxRetries: 3,
            allowFailure: true,
          }),
          [] as any[],
        )
      : Promise.resolve([] as any[]),
    safe(
      multicallRetry({
        chain: chainId,
        calls: [
          {
            address: c.glvReader,
            name: 'getAccountGlvDeposits',
            params: [c.dataStore, acct, 0n, cap],
          },
          {
            address: c.glvReader,
            name: 'getAccountGlvWithdrawals',
            params: [c.dataStore, acct, 0n, cap],
          },
        ],
        abi: GmxGlvReaderAbi as any,
        maxRetries: 3,
        allowFailure: true,
      }),
      [] as any[],
    ),
    safe(
      multicallRetry({
        chain: chainId,
        calls: [
          {
            address: c.dataStore,
            name: 'getBytes32ValuesAt',
            params: [accountDepositListKey(acct), 0n, cap],
          },
          {
            address: c.dataStore,
            name: 'getBytes32ValuesAt',
            params: [accountWithdrawalListKey(acct), 0n, cap],
          },
        ],
        abi: GmxDataStoreAbi as any,
        maxRetries: 3,
        allowFailure: true,
      }),
      [] as any[],
    ),
  ])

  // Balances
  const balances: GmxUserBalance[] = []
  tokens.forEach((t, i) => {
    const raw = balanceRes[i]
    if (isBig(raw) && raw > 0n)
      balances.push({
        token: t.address.toLowerCase(),
        kind: t.kind,
        balance: raw.toString(),
      })
  })

  // GM tickets: resolve each enumerated key via the Reader.
  const depositKeys: string[] = Array.isArray(keyRes[0]) ? keyRes[0] : []
  const withdrawalKeys: string[] = Array.isArray(keyRes[1]) ? keyRes[1] : []
  const readerCalls = [
    ...depositKeys.map((key) => ({
      address: c.reader,
      name: 'getDeposit',
      params: [c.dataStore, key],
    })),
    ...withdrawalKeys.map((key) => ({
      address: c.reader,
      name: 'getWithdrawal',
      params: [c.dataStore, key],
    })),
  ]
  const readerRes = readerCalls.length
    ? await safe(
        multicallRetry({
          chain: chainId,
          calls: readerCalls,
          abi: GmxReaderAbi as any,
          maxRetries: 3,
          allowFailure: true,
        }),
        [] as any[],
      )
    : []

  const pendingDeposits: GmxPendingDeposit[] = []
  const pendingWithdrawals: GmxPendingWithdrawal[] = []

  depositKeys.forEach((key, i) => {
    const d = readerRes[i]
    const a = d?.addresses
    if (!a?.market) return
    const n = d.numbers
    pendingDeposits.push({
      key,
      kind: 'gm',
      account: a.account.toLowerCase(),
      receiver: a.receiver.toLowerCase(),
      market: a.market.toLowerCase(),
      initialLongToken: a.initialLongToken.toLowerCase(),
      initialShortToken: a.initialShortToken.toLowerCase(),
      initialLongTokenAmount: str(n.initialLongTokenAmount),
      initialShortTokenAmount: str(n.initialShortTokenAmount),
      minOut: str(n.minMarketTokens),
      executionFee: str(n.executionFee),
      updatedAtTime: str(n.updatedAtTime),
    })
  })

  withdrawalKeys.forEach((key, i) => {
    const w = readerRes[depositKeys.length + i]
    const a = w?.addresses
    if (!a?.market) return
    const n = w.numbers
    pendingWithdrawals.push({
      key,
      kind: 'gm',
      account: a.account.toLowerCase(),
      receiver: a.receiver.toLowerCase(),
      market: a.market.toLowerCase(),
      tokenAmount: str(n.marketTokenAmount),
      minLongTokenAmount: str(n.minLongTokenAmount),
      minShortTokenAmount: str(n.minShortTokenAmount),
      executionFee: str(n.executionFee),
      updatedAtTime: str(n.updatedAtTime),
    })
  })

  // GLV tickets (batch reader returns the full props arrays).
  const glvDeposits: any[] = Array.isArray(glvRes[0]) ? glvRes[0] : []
  const glvWithdrawals: any[] = Array.isArray(glvRes[1]) ? glvRes[1] : []
  for (const d of glvDeposits) {
    const a = d?.addresses
    if (!a?.glv) continue
    const n = d.numbers
    pendingDeposits.push({
      kind: 'glv',
      account: a.account.toLowerCase(),
      receiver: a.receiver.toLowerCase(),
      market: a.market.toLowerCase(),
      glv: a.glv.toLowerCase(),
      initialLongToken: a.initialLongToken.toLowerCase(),
      initialShortToken: a.initialShortToken.toLowerCase(),
      initialLongTokenAmount: str(n.initialLongTokenAmount),
      initialShortTokenAmount: str(n.initialShortTokenAmount),
      minOut: str(n.minGlvTokens),
      executionFee: str(n.executionFee),
      updatedAtTime: str(n.updatedAtTime),
    })
  }
  for (const w of glvWithdrawals) {
    const a = w?.addresses
    if (!a?.glv) continue
    const n = w.numbers
    pendingWithdrawals.push({
      kind: 'glv',
      account: a.account.toLowerCase(),
      receiver: a.receiver.toLowerCase(),
      market: a.market.toLowerCase(),
      glv: a.glv.toLowerCase(),
      tokenAmount: str(n.glvTokenAmount),
      minLongTokenAmount: str(n.minLongTokenAmount),
      minShortTokenAmount: str(n.minShortTokenAmount),
      executionFee: str(n.executionFee),
      updatedAtTime: str(n.updatedAtTime),
    })
  }

  return {
    chainId,
    account: account.toLowerCase(),
    balances,
    pendingDeposits,
    pendingWithdrawals,
  }
}
